import React from 'react';
import './Techniques.css';
import { trackTechniqueUsage, trackEngagement } from '../utils/analytics';

const Techniques = () => {
  const techniques = [
    {
      id: 1,
      title: 'The 4-7-8 Breathing Technique',
      description: 'A powerful breathing method to instantly calm your nervous system.',
      steps: [
        'Sit comfortably and close your eyes',
        'Inhale through your nose for 4 counts',
        'Hold your breath for 7 counts',
        'Exhale slowly through your mouth for 8 counts',
        'Repeat 4-5 times'
      ],
      tips: 'Practice this technique daily, even when not angry, to build the habit.',
      icon: '🫁'
    },
    {
      id: 2,
      title: 'The STOP Technique',
      description: 'A simple method to pause and regain control during anger.',
      steps: [
        'S - Stop what you\'re doing',
        'T - Take a deep breath',
        'O - Observe your thoughts and feelings',
        'P - Proceed mindfully'
      ],
      tips: 'Use this whenever you feel anger rising to prevent impulsive reactions.',
      icon: '⏹️'
    },
    {
      id: 3,
      title: 'Progressive Muscle Relaxation',
      description: 'Systematically tense and relax muscle groups to release physical tension.',
      steps: [
        'Find a quiet, comfortable place',
        'Start with your toes and work up to your head',
        'Tense each muscle group for 5 seconds',
        'Release and feel the relaxation for 10 seconds',
        'Move to the next muscle group'
      ],
      tips: 'Practice this technique regularly to reduce overall stress levels.',
      icon: '💪'
    },
    {
      id: 4,
      title: 'Cognitive Distancing',
      description: 'Step back from your thoughts to gain perspective on the situation.',
      steps: [
        'Recognize that thoughts are just thoughts',
        'Ask yourself: "Is this thought helpful?"',
        'Consider alternative perspectives',
        'Focus on what you can control',
        'Choose a constructive response'
      ],
      tips: 'Remember that thoughts are not facts - you can choose how to respond.',
      icon: '🧠'
    },
    {
      id: 5,
      title: 'The 10-10-10 Rule',
      description: 'Evaluate how your anger will matter in 10 minutes, 10 months, and 10 years.',
      steps: [
        'When angry, pause and ask: "Will this matter in 10 minutes?"',
        'Then ask: "Will this matter in 10 months?"',
        'Finally ask: "Will this matter in 10 years?"',
        'Based on your answers, choose your response',
        'Focus on what truly matters'
      ],
      tips: 'This technique helps you gain perspective and avoid overreacting.',
      icon: '⏰'
    },
    {
      id: 6,
      title: 'Compassionate Self-Talk',
      description: 'Replace harsh self-criticism with kind, understanding inner dialogue.',
      steps: [
        'Notice when you\'re being self-critical',
        'Imagine talking to a dear friend in the same situation',
        'Use the same kind, supportive language with yourself',
        'Acknowledge your feelings without judgment',
        'Offer yourself comfort and encouragement'
      ],
      tips: 'Practice self-compassion daily to build emotional resilience.',
      icon: '💝'
    }
  ];

  const handlePracticeClick = (techniqueName) => {
    trackTechniqueUsage(techniqueName);
    trackEngagement('techniques', 'practice_clicked');
  };

  return (
    <section className="techniques">
      <div className="techniques-container">
        <div className="techniques-header">
          <h2 className="techniques-title">Proven Anger Management Techniques</h2>
          <p className="techniques-subtitle">
            Master these evidence-based techniques to transform how you handle anger 
            and create lasting positive change in your life.
          </p>
        </div>
        
        <div className="techniques-grid">
          {techniques.map(technique => (
            <div key={technique.id} className="technique-card">
              <div className="technique-header">
                <div className="technique-icon">{technique.icon}</div>
                <h3 className="technique-title">{technique.title}</h3>
              </div>
              <p className="technique-description">{technique.description}</p>
              
              <div className="technique-steps">
                <h4>Steps:</h4>
                <ol>
                  {technique.steps.map((step, index) => (
                    <li key={index}>{step}</li>
                  ))}
                </ol>
              </div>
              
              <div className="technique-tip">
                <strong>💡 Tip:</strong> {technique.tips}
              </div>
              
              <button 
                className="technique-practice"
                onClick={() => handlePracticeClick(technique.title)}
              >
                Practice Now
              </button>
            </div>
          ))}
        </div>
        
        <div className="techniques-footer">
          <h3>Remember: Practice Makes Progress</h3>
          <p>
            These techniques become more effective with regular practice. Start with one technique 
            that resonates with you and practice it daily. Over time, you'll develop the skills 
            to manage anger more effectively and create a more peaceful life.
          </p>
        </div>
      </div>
    </section>
  );
};

export default Techniques; 